"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { CUSTOM_THEME_STORAGE_KEY, THEME_STORAGE_KEY } from "@/lib/theme-boot-script";
import { CustomTheme, ThemeColors, colorToCssVar, getThemeById, presetThemes } from "@/lib/themes";
import { useThemeStore } from "@/stores/theme-store";
export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";
interface ThemeContextType {
  // Legacy mode-based theme (light/dark/system)
  theme: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;

  // Preset theme support
  customTheme: CustomTheme | null;
  customThemes: CustomTheme[];
  setCustomTheme: (theme: CustomTheme | null) => void;
}
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function getStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  // Default to system mode so it respects the user's OS preference
  return "system";
}
function getStoredCustomTheme(): CustomTheme | null {
  if (typeof window === "undefined") return null;
  const storedId = localStorage.getItem(CUSTOM_THEME_STORAGE_KEY);
  if (!storedId) return null;

  // Check preset themes
  const presetTheme = getThemeById(storedId);
  if (presetTheme) return presetTheme;
  return null;
}
function applyCustomThemeColors(colors: ThemeColors) {
  const root = document.documentElement;
  Object.entries(colors).forEach(([key, value]) => {
    const cssVar = colorToCssVar[key as keyof ThemeColors];
    if (cssVar) {
      root.style.setProperty(cssVar, value);
    }
  });
}
function clearCustomThemeColors() {
  const root = document.documentElement;
  Object.values(colorToCssVar).forEach(cssVar => {
    root.style.removeProperty(cssVar);
  });
}
export function ThemeProvider(t0) {
  const $ = _c(30);
  const {
    children
  } = t0;
  const [theme, setThemeState] = useState("system");
  const [resolvedTheme, setResolvedTheme] = useState("light");
  const [customTheme, setCustomThemeState] = useState(null);
  const [mounted, setMounted] = useState(false);
  const setResolvedThemeInStore = useThemeStore(_temp);
  let t1;
  let t2;
  if ($[0] !== setResolvedThemeInStore) {
    t1 = () => {
      const storedTheme = getStoredTheme();
      const storedCustomTheme = getStoredCustomTheme();
      setThemeState(storedTheme);
      setCustomThemeState(storedCustomTheme);
      const initialResolved = storedTheme === "system" ? getSystemTheme() : storedTheme;
      setResolvedThemeInStore(initialResolved);
      setMounted(true);
    };
    t2 = [setResolvedThemeInStore];
    $[0] = setResolvedThemeInStore;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== customTheme || $[4] !== mounted || $[5] !== setResolvedThemeInStore || $[6] !== theme) {
    t3 = () => {
      if (!mounted) {
        return;
      }
      let resolved;
      if (customTheme) {
        resolved = customTheme.baseMode;
        applyCustomThemeColors(customTheme.colors);
      } else {
        clearCustomThemeColors();
        resolved = theme === "system" ? getSystemTheme() : theme;
      }
      setResolvedTheme(resolved);
      setResolvedThemeInStore(resolved);
      const root = document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(resolved);
      root.style.colorScheme = resolved;
    };
    t4 = [theme, customTheme, mounted, setResolvedThemeInStore];
    $[3] = customTheme;
    $[4] = mounted;
    $[5] = setResolvedThemeInStore;
    $[6] = theme;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== customTheme || $[10] !== mounted || $[11] !== setResolvedThemeInStore || $[12] !== theme) {
    t5 = () => {
      if (!mounted || theme !== "system" || customTheme) {
        return;
      }
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = e => {
        const newTheme = e.matches ? "dark" : "light";
        setResolvedTheme(newTheme);
        setResolvedThemeInStore(newTheme);
        const root_0 = document.documentElement;
        root_0.classList.remove("light", "dark");
        root_0.classList.add(newTheme);
        root_0.style.colorScheme = newTheme;
      };
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    };
    t6 = [theme, customTheme, mounted, setResolvedThemeInStore];
    $[9] = customTheme;
    $[10] = mounted;
    $[11] = setResolvedThemeInStore;
    $[12] = theme;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useEffect(t5, t6);
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = newTheme_0 => {
      setThemeState(newTheme_0);
      localStorage.setItem(THEME_STORAGE_KEY, newTheme_0);
      setCustomThemeState(null);
      localStorage.removeItem(CUSTOM_THEME_STORAGE_KEY);
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const setTheme = t7;
  let t8;
  if ($[16] !== resolvedTheme) {
    t8 = () => {
      setTheme(resolvedTheme === "light" ? "dark" : "light");
    };
    $[16] = resolvedTheme;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const toggleTheme = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = theme_0 => {
      setCustomThemeState(theme_0);
      if (theme_0) {
        localStorage.setItem(CUSTOM_THEME_STORAGE_KEY, theme_0.id);
      } else {
        localStorage.removeItem(CUSTOM_THEME_STORAGE_KEY);
      }
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const setCustomTheme = t9;
  if (!mounted) {
    let t10;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        theme: "system",
        resolvedTheme: "light",
        setTheme: _temp2,
        toggleTheme: _temp3,
        customTheme: null,
        customThemes: presetThemes,
        setCustomTheme: _temp4
      };
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== children) {
      t11 = <ThemeContext.Provider value={t10}>{children}</ThemeContext.Provider>;
      $[20] = children;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    return t11;
  }
  let t10;
  if ($[22] !== customTheme || $[23] !== resolvedTheme || $[24] !== theme || $[25] !== toggleTheme) {
    t10 = {
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme,
      customTheme,
      customThemes: presetThemes,
      setCustomTheme
    };
    $[22] = customTheme;
    $[23] = resolvedTheme;
    $[24] = theme;
    $[25] = toggleTheme;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== children || $[28] !== t10) {
    t11 = <ThemeContext.Provider value={t10}>{children}</ThemeContext.Provider>;
    $[27] = children;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function _temp4() {}
function _temp3() {}
function _temp2() {}
function _temp(state) {
  return state.setResolvedTheme;
}
const defaultThemeContext: ThemeContextType = {
  theme: "system",
  resolvedTheme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
  customTheme: null,
  customThemes: presetThemes,
  setCustomTheme: () => {}
};
export function useTheme() {
  const context = useContext(ThemeContext);
  return context ?? defaultThemeContext;
}